import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAppUserInactive } from '../services/activeAppUser';
import { hasActiveAuthSession, isSessionTimedOut } from '../utils/authSession';
import { AccessControlProvider, useAccessControl } from '../contexts/AccessControlContext';
import { RequireAccess } from '../rbac/guards';
import { DEFAULT_POST_AUTH_PATH, resolveLandingPath } from '../rbac/landingPage';
import { hasNormalDashboardAccess, hasSgDashboardAccess, resolveDashboardPath } from '../rbac/dashboardAccess';
import { ROUTE_ACCESS } from '../rbac/routes';
import { tasksListPathForRole } from '../rbac/tasksListPath';
import SessionTimeoutPage from '../pages/auth/SessionTimeoutPage';
import UnauthorizedAccessPage from '../pages/auth/UnauthorizedAccessPage';
import LoggedOutPage from '../pages/auth/LoggedOutPage';
import NotFoundPage from '../pages/NotFoundPage';
import { AppShell } from '../ui/AppShell';
import { CaseRequestAppShell } from '../ui/CaseRequestAppShell';
import TasksCasesPage from '../pages/tasks/TasksCasesPage';
import NotificationsPage from '../pages/notifications/NotificationsPage';
import ActiveCaseScenariosPage from '../pages/case-scenarios/CaseScenariosPage';
import CaseScenarioPdfPage from '../pages/case-scenarios/CaseScenarioPdfPage';
import TaskCaseDetailsPage from '../pages/tasks/TaskCaseDetailsPage';
import AssignTasksPage from '../pages/tasks/AssignTasksPage';
import EscalationTasksPage from '../pages/tasks/EscalationTasksPage';
import AttendingTasksPage from '../pages/tasks/AttendingTasksPage';
import ManagementAssistantCaseDetailsPage from '../pages/tasks/ManagementAssistantCaseDetailsPage';
import MailRoomRequestsPage from '../pages/mailRoom/MailRoomRequestsPage';
import CompletedFilesPage from '../pages/recordRoom/CompletedFilesPage';
import QrScanningLogPage from '../pages/recordRoom/QrScanningLogPage';
import IntranetUsersPage from '../pages/admin/IntranetUsersPage';
import IntranetUserFormPage from '../pages/admin/IntranetUserFormPage';
import ExtranetUsersPage from '../pages/admin/ExtranetUsersPage';
import ExtranetUserFormPage from '../pages/admin/ExtranetUserFormPage';
import RolesPermissionsPage from '../pages/admin/RolesPermissionsPage';
import RoleFormPage from '../pages/admin/RoleFormPage';
import UserCategoriesPage from '../pages/admin/UserCategoriesPage';
import UserCategoryFormPage from '../pages/admin/UserCategoryFormPage';
import DashboardsPage from '../pages/dashboards/DashboardsPage';
import SgDashboardPage from '../pages/dashboards/SgDashboardPage';
import OperationalDashboardPage from '../pages/dashboards/OperationalDashboardPage';
import AuditLogsPage from '../pages/audit/AuditLogsPage';
import UserPerformanceReportPage from '../pages/reports/UserPerformanceReportPage';
import CaseTrackingReportPage from '../pages/reports/CaseTrackingReportPage';
import WorkflowPerformanceReportPage from '../pages/reports/WorkflowPerformanceReportPage';
import WorkflowTemplatesPage from '../pages/workflow/WorkflowTemplatesPage';
import KpiConfigurationPage from '../pages/workflow/KpiConfigurationPage';
import EscalationRulesPage from '../pages/workflow/EscalationRulesPage';
import WorkflowTemplateDesignerPage from '../pages/workflow/WorkflowTemplateDesignerPage';
import SubCategoriesPage from '../pages/metadata/SubCategoriesPage';
import SubCategoryFormPage from '../pages/metadata/SubCategoryFormPage';
import CourtsPage from '../pages/metadata/CourtsPage';
import CourtFormPage from '../pages/metadata/CourtFormPage';
import LanguagesPage from '../pages/metadata/LanguagesPage';
import LanguageFormPage from '../pages/metadata/LanguageFormPage';
import CountriesPage from '../pages/metadata/CountriesPage';
import CountryFormPage from '../pages/metadata/CountryFormPage';
import NationalitiesPage from '../pages/metadata/NationalitiesPage';
import NationalityFormPage from '../pages/metadata/NationalityFormPage';
import PoliceStationsPage from '../pages/metadata/PoliceStationsPage';
import PoliceStationFormPage from '../pages/metadata/PoliceStationFormPage';
import LetterTemplatesPage from '../pages/metadata/LetterTemplatesPage';
import LetterTemplateFormPage from '../pages/metadata/LetterTemplateFormPage';
import SubsidiaryTasksPage from '../pages/metadata/SubsidiaryTasksPage';
import SubsidiaryTaskFormPage from '../pages/metadata/SubsidiaryTaskFormPage';
import CaseScenariosPage from '../pages/metadata/CaseScenariosPage';
import CaseScenarioFormPage from '../pages/metadata/CaseScenarioFormPage';
import LegalKnowledgeArchivePage from '../pages/legalKnowledge/LegalKnowledgeArchivePage';
import ExtractedCaseOutcomesPage from '../pages/legalKnowledge/ExtractedCaseOutcomesPage';
import ViewExtractedCaseOutcomePage from '../pages/legalKnowledge/ViewExtractedCaseOutcomePage';
import ViewArchivedCaseDetailsPage from '../pages/legalKnowledge/ViewArchivedCaseDetailsPage';
import LKADashboardPage from '../pages/legalKnowledge/LKADashboardPage';
import UserProfilePage from '../pages/profile/UserProfilePage';
import ViewRequestPage from '../pages/ViewRequestPage';
import CaseRequestLayout from '../layouts/CaseRequestLayout';
import {
  LegacyWizardRedirect,
  WizardReviewPage,
  WizardStep1Page,
  WizardStep2Page,
  WizardStep3Page,
  WizardStep4Page,
  WizardStep5Page,
  WizardStep6Page,
  WizardStep7Page,
  WizardStep8Page,
  WizardStep9Page,
  WizardStep10Page,
  WizardStep11Page,
  WizardStep12Page,
} from './WizardRoutePages';
import { isJustLoggedOut } from '../utils/authStorage';

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { user, signIn } = useAuth();
  const location = useLocation();
  const timedOut = isSessionTimedOut();
  const authed = Boolean(user) && hasActiveAuthSession();

  useEffect(() => {
    if (timedOut || authed) return;
    // If a logout just happened in this browser, the path currently mounted belongs to the
    // PREVIOUS user's session. Don't ask Keycloak to return here for whoever logs in next —
    // fall back to the default path so permission-based landing-page resolution decides.
    const target = isJustLoggedOut() ? undefined : location.pathname + location.search;
    void signIn(target);
  }, [timedOut, authed, signIn, location.pathname, location.search]);

  if (timedOut) {
    return <Navigate to="/session-timeout" replace />;
  }
  if (isAppUserInactive()) {
    return (
      <Navigate
        to="/unauthorized"
        replace
        state={{ redirectTo: location.pathname + location.search }}
      />
    );
  }
  if (!authed) {
    return null;
  }
  return children;
}

function PermissionLandingRedirectInner() {
  const ac = useAccessControl();

  if (ac.loading) {
    return <div style={{ padding: 16 }}>Loading…</div>;
  }

  if (ac.status === 'error') {
    return <Navigate to="/unauthorized" replace />;
  }

  const target = resolveLandingPath({
    roleCode: ac.roleCode,
    permissions: ac.permissions,
  });

  return <Navigate to={target} replace />;
}

function CreateCaseRequestEntryRedirect() {
  const ac = useAccessControl();

  if (ac.loading) {
    return <div style={{ padding: 16 }}>Loading…</div>;
  }

  return (
    <Navigate
      to="/requests/new/step-1"
      replace
      state={{ returnTo: tasksListPathForRole(ac.roleCode) }}
    />
  );
}

function DashboardEntryPage() {
  const ac = useAccessControl();

  if (ac.loading) {
    return <div style={{ padding: 16 }}>Loading…</div>;
  }

  if (hasSgDashboardAccess(ac.permissions)) {
    return <Navigate to="/dashboards/sg" replace />;
  }

  if (hasNormalDashboardAccess(ac.permissions)) {
    return <Navigate to="/dashboards/operational" replace />;
  }

  return <DashboardsPage />;
}

function SgDashboardEntryPage() {
  const ac = useAccessControl();

  if (ac.loading) {
    return <div style={{ padding: 16 }}>Loading…</div>;
  }

  if (!hasSgDashboardAccess(ac.permissions)) {
    const fallback = resolveDashboardPath(ac.permissions);
    return <Navigate to={fallback ?? '/dashboards'} replace />;
  }

  return <SgDashboardPage />;
}

function PermissionLandingRedirect() {
  const { user, signIn } = useAuth();
  const timedOut = isSessionTimedOut();
  const authed = Boolean(user) && hasActiveAuthSession();

  useEffect(() => {
    if (timedOut || authed) return;
    void signIn(DEFAULT_POST_AUTH_PATH);
  }, [timedOut, authed, signIn]);

  if (timedOut) {
    return <Navigate to="/session-timeout" replace />;
  }
  if (isAppUserInactive()) {
    return <Navigate to="/unauthorized" replace />;
  }
  if (!authed) {
    return null;
  }

  return (
    <AccessControlProvider>
      <PermissionLandingRedirectInner />
    </AccessControlProvider>
  );
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/session-timeout" element={<SessionTimeoutPage />} />
      <Route path="/unauthorized" element={<UnauthorizedAccessPage />} />
      <Route path="/logged-out" element={<LoggedOutPage />} />

      <Route
        element={
          <RequireAuth>
            <AccessControlProvider>
              <AppShell />
            </AccessControlProvider>
          </RequireAuth>
        }
      >
        <Route
          path="/tasks"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/tasks']}>
              <TasksCasesPage />
            </RequireAccess>
          }
        />
        <Route
          path="/tasks/assign"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/tasks/assign']}>
              <AssignTasksPage />
            </RequireAccess>
          }
        />

        <Route
          path="/tasks/escalation"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/tasks/escalation']}>
              <EscalationTasksPage />
            </RequireAccess>
          }
        />
        <Route
          path="/tasks/attending"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/tasks/attending']}>
              <AttendingTasksPage />
            </RequireAccess>
          }
        />
        <Route
          path="/tasks/case-details"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/tasks/case-details']}>
              <ManagementAssistantCaseDetailsPage />
            </RequireAccess>
          }
        />
        <Route
          path="/tasks/:taskId"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/tasks/:taskId']}>
              <TaskCaseDetailsPage />
            </RequireAccess>
          }
        />
        <Route
          path="/tasks/create-case-request"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/tasks/create-case-request']}>
              <CreateCaseRequestEntryRedirect />
            </RequireAccess>
          }
        />
        <Route
          path="/notifications"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/notifications']}>
              <NotificationsPage />
            </RequireAccess>
          }
        />
        <Route
          path="/case-scenarios"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/case-scenarios']}>
              <ActiveCaseScenariosPage />
            </RequireAccess>
          }
        />
        <Route
          path="/case-scenarios/pdf"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/case-scenarios/pdf']}>
              <CaseScenarioPdfPage />
            </RequireAccess>
          }
        />
        <Route
          path="/mail-room/requests"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/mail-room/requests']}>
              <MailRoomRequestsPage />
            </RequireAccess>
          }
        />
        <Route
          path="/record-room/completed-files"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/record-room/completed-files']}>
              <CompletedFilesPage />
            </RequireAccess>
          }
        />
        <Route
          path="/record-room/qr-scanning-log"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/record-room/qr-scanning-log']}>
              <QrScanningLogPage />
            </RequireAccess>
          }
        />
        <Route
          path="/record-room/qr-scanning-logs"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/record-room/qr-scanning-log']}>
              <QrScanningLogPage />
            </RequireAccess>
          }
        />
        <Route
          path="/legal-knowledge/archive"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/legal-knowledge/archive']}>
              <LegalKnowledgeArchivePage />
            </RequireAccess>
          }
        />
        <Route
          path="/legal-knowledge/archive/:id"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/legal-knowledge/archive/:id']}>
              <ViewArchivedCaseDetailsPage />
            </RequireAccess>
          }
        />
        <Route
          path="/legal-knowledge/archive/management/outcomes"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/legal-knowledge/archive/management/outcomes']}>
              <ExtractedCaseOutcomesPage />
            </RequireAccess>
          }
        />
        <Route
          path="/legal-knowledge/archive/management/outcomes/:id"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/legal-knowledge/archive/management/outcomes/:id']}>
              <ViewExtractedCaseOutcomePage />
            </RequireAccess>
          }
        />
        <Route
          path="/legal-knowledge/archive/management/archived-case-details/:id"
          element={null}
        />
        <Route
          path="/legal-knowledge/archive/management/dashboard"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/legal-knowledge/archive/management/dashboard']}>
              <LKADashboardPage />
            </RequireAccess>
          }
        />
        <Route
          path="/admin/intranet-users"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/admin/intranet-users']}>
              <IntranetUsersPage />
            </RequireAccess>
          }
        />
        <Route
          path="/admin/intranet-users/new"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/admin/intranet-users/new']}>
              <IntranetUserFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/admin/intranet-users/:userId/edit"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/admin/intranet-users/:userId/edit']}>
              <IntranetUserFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/admin/user-categories"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/admin/user-categories']}>
              <UserCategoriesPage />
            </RequireAccess>
          }
        />
        <Route
          path="/admin/user-categories/new"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/admin/user-categories/new']}>
              <UserCategoryFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/admin/user-categories/:categoryAllocationId/edit"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/admin/user-categories/:categoryAllocationId/edit']}>
              <UserCategoryFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/admin/extranet-users"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/admin/extranet-users']}>
              <ExtranetUsersPage />
            </RequireAccess>
          }
        />
        <Route
          path="/admin/extranet-users/new"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/admin/extranet-users/new']}>
              <ExtranetUserFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/admin/extranet-users/:userId/edit"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/admin/extranet-users/:userId/edit']}>
              <ExtranetUserFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/admin/roles-permissions"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/admin/roles-permissions']}>
              <RolesPermissionsPage />
            </RequireAccess>
          }
        />
        <Route
          path="/admin/roles-permissions/new"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/admin/roles-permissions/new']}>
              <RoleFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/admin/roles-permissions/:roleId/edit"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/admin/roles-permissions/:roleId/edit']}>
              <RoleFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/admin/roles-permissions/:roleId/view"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/admin/roles-permissions/:roleId/view']}>
              <RoleFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/dashboards/sg"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/dashboards/sg']}>
              <SgDashboardEntryPage />
            </RequireAccess>
          }
        />
        <Route
          path="/dashboards/operational"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/dashboards/operational']}>
              <OperationalDashboardPage />
            </RequireAccess>
          }
        />
        <Route
          path="/dashboards"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/dashboards']}>
              <DashboardEntryPage />
            </RequireAccess>
          }
        />
        <Route
          path="/audit-logs"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/audit-logs']}>
              <AuditLogsPage />
            </RequireAccess>
          }
        />
        <Route path="/reports" element={<Navigate to="/reports/user-performance" replace />} />
        <Route
          path="/reports/user-performance"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/reports/user-performance']}>
              <UserPerformanceReportPage />
            </RequireAccess>
          }
        />
        <Route
          path="/reports/case-tracking"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/reports/case-tracking']}>
              <CaseTrackingReportPage />
            </RequireAccess>
          }
        />
        <Route
          path="/reports/workflow-performance"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/reports/workflow-performance']}>
              <WorkflowPerformanceReportPage />
            </RequireAccess>
          }
        />
        <Route
          path="/workflow/templates"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/workflow/templates']}>
              <WorkflowTemplatesPage />
            </RequireAccess>
          }
        />
        <Route
          path="/workflow/templates/designer"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/workflow/templates/designer']}>
              <WorkflowTemplateDesignerPage />
            </RequireAccess>
          }
        />
        <Route
          path="/workflow/kpi-configuration"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/workflow/kpi-configuration']}>
              <KpiConfigurationPage />
            </RequireAccess>
          }
        />
        <Route
          path="/workflow/escalation-rules"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/workflow/escalation-rules']}>
              <EscalationRulesPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/subcategories"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/subcategories']}>
              <SubCategoriesPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/subcategories/new"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/subcategories/new']}>
              <SubCategoryFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/subcategories/:subCategoryId/edit"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/subcategories/:subCategoryId/edit']}>
              <SubCategoryFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/courts"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/courts']}>
              <CourtsPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/courts/new"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/courts/new']}>
              <CourtFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/courts/:courtId/edit"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/courts/:courtId/edit']}>
              <CourtFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/languages"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/languages']}>
              <LanguagesPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/countries"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/countries']}>
              <CountriesPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/languages/new"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/languages/new']}>
              <LanguageFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/countries/new"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/countries/new']}>
              <CountryFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/languages/:languageId/edit"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/languages/:languageId/edit']}>
              <LanguageFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/countries/:countryId/edit"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/countries/:countryId/edit']}>
              <CountryFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/nationalities"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/nationalities']}>
              <NationalitiesPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/nationalities/new"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/nationalities/new']}>
              <NationalityFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/nationalities/:nationalityId/edit"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/nationalities/:nationalityId/edit']}>
              <NationalityFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/police-stations"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/police-stations']}>
              <PoliceStationsPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/police-stations/new"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/police-stations/new']}>
              <PoliceStationFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/police-stations/:policeStationId/edit"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/police-stations/:policeStationId/edit']}>
              <PoliceStationFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/letter-templates"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/letter-templates']}>
              <LetterTemplatesPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/letter-templates/:letterTemplateId/edit"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/letter-templates/:letterTemplateId/edit']}>
              <LetterTemplateFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/subsidiary-tasks"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/subsidiary-tasks']}>
              <SubsidiaryTasksPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/subsidiary-tasks/new"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/subsidiary-tasks/new']}>
              <SubsidiaryTaskFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/subsidiary-tasks/:subsidiaryTaskId/edit"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/subsidiary-tasks/:subsidiaryTaskId/edit']}>
              <SubsidiaryTaskFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/case-scenarios"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/case-scenarios']}>
              <CaseScenariosPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/case-scenarios/new"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/case-scenarios/new']}>
              <CaseScenarioFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/metadata/case-scenarios/:caseScenarioId/edit"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/metadata/case-scenarios/:caseScenarioId/edit']}>
              <CaseScenarioFormPage />
            </RequireAccess>
          }
        />
        <Route
          path="/profile"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/profile']}>
              <UserProfilePage />
            </RequireAccess>
          }
        />
      </Route>

      <Route
        element={
          <RequireAuth>
            <AccessControlProvider>
              <CaseRequestAppShell />
            </AccessControlProvider>
          </RequireAuth>
        }
      >
        <Route path="/requests" element={<Navigate to="/tasks" replace />} />
        <Route
          path="/requests/:caseRequestId"
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/requests']}>
              <ViewRequestPage />
            </RequireAccess>
          }
        />
        <Route
          element={
            <RequireAccess requirement={ROUTE_ACCESS['/requests/new']}>
              <CaseRequestLayout />
            </RequireAccess>
          }
        >
          <Route path="/requests/new/step-1/:caseRequestId?" element={<WizardStep1Page />} />
          <Route path="/requests/new/step-2/:caseRequestId?" element={<WizardStep2Page />} />
          <Route path="/requests/new/step-3/:caseRequestId?" element={<WizardStep3Page />} />
          <Route path="/requests/new/step-4/:caseRequestId?" element={<WizardStep4Page />} />
          <Route path="/requests/new/step-5/:caseRequestId?" element={<WizardStep5Page />} />
          <Route path="/requests/new/step-6/:caseRequestId?" element={<WizardStep6Page />} />
          <Route path="/requests/new/step-7/:caseRequestId?" element={<WizardStep7Page />} />
          <Route path="/requests/new/step-8/:caseRequestId?" element={<WizardStep8Page />} />
          <Route path="/requests/new/step-9/:caseRequestId?" element={<WizardStep9Page />} />
          <Route path="/requests/new/step-10/:caseRequestId?" element={<WizardStep10Page />} />
          <Route path="/requests/new/step-11/:caseRequestId?" element={<WizardStep11Page />} />
          <Route path="/requests/new/step-12/:caseRequestId?" element={<WizardStep12Page />} />
          <Route path="/requests/new/review/:caseRequestId?" element={<WizardReviewPage />} />
        </Route>
        <Route path="/requests/new/civil-advise/*" element={<LegacyWizardRedirect />} />
        <Route path="/requests/new/civil-litigation/*" element={<LegacyWizardRedirect />} />
      </Route>

      <Route path="/" element={<PermissionLandingRedirect />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
